import React, { useEffect, useState } from 'react';
import { useReadContract, useReadContracts } from 'wagmi';
import { formatUnits } from 'viem';
import { FaChartPie, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { vaultConfig, ADAPTER_ABI } from '../config/vaultConfig-v2';
import './VaultAllocations.css';

// Protocol info for our 4 configured adapters
const PROTOCOL_INFO = {
  [vaultConfig.adapters.aave.address.toLowerCase()]: {
    name: 'Aave V3',
    color: '#B6509E',
    description: 'Leading lending protocol',
    icon: '🏦',
  },
  [vaultConfig.adapters.euler.address.toLowerCase()]: {
    name: 'Euler Finance',
    color: '#4B7BEC',
    description: 'Permissionless lending',
    icon: '📊',
  },
  [vaultConfig.adapters.silo.address.toLowerCase()]: {
    name: 'Silo Finance',
    color: '#00D4FF',
    description: 'Isolated lending markets',
    icon: '🌾',
  },
  [vaultConfig.adapters.fortyAcres.address.toLowerCase()]: {
    name: '40 Acres Finance',
    color: '#4CAF50',
    description: 'Yield optimization',
    icon: '🌳',
  },
};

function VaultAllocationsV2() {
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalAssets, setTotalAssets] = useState(0n);

  // Get adapters from VaultCore
  const { data: adaptersList } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'getAdapters',
    watch: true,
  });

  // Get total allocation basis points
  const { data: totalAllocationBps } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'totalAllocationBps',
    watch: true,
  });

  // Read allocations for each adapter
  const allocationContracts = adaptersList?.map(adapter => ({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'adapterAllocations',
    args: [adapter],
  })) || [];

  const { data: adapterAllocationsData } = useReadContracts({
    contracts: allocationContracts,
    watch: true,
    enabled: adaptersList && adaptersList.length > 0,
  });

  // Read health status for each adapter
  const healthContracts = adaptersList?.map(adapter => ({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'adapterHealthStatus',
    args: [adapter],
  })) || [];

  const { data: adapterHealthData } = useReadContracts({
    contracts: healthContracts,
    watch: true,
    enabled: adaptersList && adaptersList.length > 0,
  });

  // Read total assets from each adapter
  const adapterContracts = adaptersList?.map(adapter => ({
    address: adapter,
    abi: ADAPTER_ABI,
    functionName: 'totalAssets',
  })) || [];

  const { data: adapterAssets } = useReadContracts({
    contracts: adapterContracts,
    watch: true,
    enabled: adaptersList && adaptersList.length > 0,
  });

  // Read emergency idle funds
  const { data: emergencyIdleFunds } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'emergencyIdleFunds',
    watch: true,
  });

  useEffect(() => {
    console.log('Adapters list:', adaptersList);
    console.log('Adapter allocations:', adapterAllocationsData);
    console.log('Adapter assets:', adapterAssets);
    console.log('Adapter health:', adapterHealthData);
    console.log('Emergency idle funds:', emergencyIdleFunds);

    if (adaptersList && adapterAssets && adapterAllocationsData) {
      const assets = adapterAssets.map(result => 
        result && result.status === 'success' ? result.result : 0n
      );
      
      const total = assets.reduce((sum, asset) => sum + asset, 0n) + (emergencyIdleFunds || 0n);
      setTotalAssets(total);
      
      const allocs = adaptersList.map((adapter, index) => {
        const adapterLower = adapter.toLowerCase();
        const protocolInfo = PROTOCOL_INFO[adapterLower] || {
          name: `Protocol ${index + 1}`,
          color: '#808080',
          description: 'Unknown protocol',
          icon: '🔧',
        };
        
        const assetAmount = assets[index];
        const actualPercentage = total > 0n 
          ? (Number(assetAmount) / Number(total)) * 100 
          : 0;
        
        // Get allocations from contract
        let targetAlloc = 25; // default
        let minAlloc = 0;
        let maxAlloc = 100;
        
        if (adapterAllocationsData[index] && adapterAllocationsData[index].status === 'success') {
          const [targetBps, minBps, maxBps] = adapterAllocationsData[index].result;
          targetAlloc = Number(targetBps) / 100;
          minAlloc = Number(minBps) / 100;
          maxAlloc = Number(maxBps) / 100;
        }
        
        // Get health status
        let isHealthy = true;
        if (adapterHealthData && adapterHealthData[index] && adapterHealthData[index].status === 'success') {
          const healthStatus = adapterHealthData[index].result;
          isHealthy = healthStatus[3]; // isHealthy is the 4th element
        }
        
        return {
          address: adapter,
          ...protocolInfo,
          amount: assetAmount,
          percentage: actualPercentage,
          targetAllocation: targetAlloc,
          minAllocation: minAlloc,
          maxAllocation: maxAlloc,
          isHealthy,
        };
      });
      
      setAllocations(allocs);
      setLoading(false);
    } else if (adaptersList && adaptersList.length === 0) {
      setLoading(false);
    }
  }, [adaptersList, adapterAssets, adapterAllocationsData, adapterHealthData, emergencyIdleFunds]);

  // Show loading state
  if (loading) {
    return (
      <div className="vault-allocations">
        <div className="allocations-header">
          <FaChartPie className="section-icon" />
          <h3>Protocol Allocations</h3>
        </div>
        <div className="loading">Loading allocations...</div>
      </div>
    );
  }

  // Show a message if no adapters configured
  if (!adaptersList || adaptersList.length === 0) {
    return (
      <div className="vault-allocations">
        <div className="allocations-header">
          <FaChartPie className="section-icon" />
          <h3>Protocol Allocations</h3>
        </div>
        <div className="no-allocations">
          <p>No adapters configured in VaultCore.</p>
          <p>Contact admin to add yield strategies.</p>
        </div>
      </div>
    );
  }

  // Show if no funds allocated
  if (totalAssets === 0n) {
    return (
      <div className="vault-allocations">
        <div className="allocations-header">
          <FaChartPie className="section-icon" />
          <h3>Protocol Allocations</h3>
          <span className="allocation-count">{allocations.length} Protocols Configured</span>
        </div>
        <div className="no-allocations">
          <p>No funds allocated yet.</p>
          <p>Deposit USDC to start earning yield across {allocations.length} protocols.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vault-allocations">
      <div className="allocations-header">
        <FaChartPie className="section-icon" />
        <h3>Protocol Allocations</h3>
        <span className="allocation-count">{allocations.length} Active Protocols</span>
      </div>

      <div className="allocations-chart">
        <div className="pie-chart">
          <svg viewBox="0 0 100 100" className="donut-chart">
            {allocations.map((alloc, index) => {
              const offset = allocations
                .slice(0, index)
                .reduce((sum, a) => sum + a.percentage, 0);
              const circumference = 2 * Math.PI * 30;
              const strokeDasharray = `${(alloc.percentage / 100) * circumference} ${circumference}`;
              const rotation = (offset / 100) * 360;
              
              return (
                <circle
                  key={alloc.address}
                  cx="50"
                  cy="50"
                  r="30"
                  fill="none"
                  stroke={alloc.color}
                  strokeWidth="15"
                  strokeDasharray={strokeDasharray}
                  transform={`rotate(${rotation - 90} 50 50)`}
                  className="chart-segment"
                />
              );
            })}
            {/* Add segment for emergency idle funds if any */}
            {emergencyIdleFunds > 0n && (
              <circle
                cx="50"
                cy="50"
                r="30"
                fill="none"
                stroke="#FF6B6B"
                strokeWidth="15"
                strokeDasharray={`${((Number(emergencyIdleFunds) / Number(totalAssets)) * 100 / 100) * 2 * Math.PI * 30} ${2 * Math.PI * 30}`}
                transform={`rotate(${(allocations.reduce((sum, a) => sum + a.percentage, 0) / 100) * 360 - 90} 50 50)`}
                className="chart-segment"
              />
            )}
          </svg>
          <div className="chart-center">
            <span className="chart-label">Total TVL</span>
            <span className="chart-value">
              ${Number(formatUnits(totalAssets, 6)).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="allocations-list">
          {allocations.map((alloc) => (
            <div key={alloc.address} className="allocation-item" style={{'--color': alloc.color}}>
              <div className="allocation-color" style={{ backgroundColor: alloc.color }}>
                <span className="protocol-icon">{alloc.icon}</span>
              </div>
              <div className="allocation-info">
                <div className="allocation-header">
                  <span className="allocation-name">{alloc.name}</span>
                  {alloc.isHealthy ? (
                    <FaCheckCircle className="health-icon healthy" title="Healthy" />
                  ) : (
                    <FaExclamationCircle className="health-icon unhealthy" title="Needs attention" />
                  )}
                </div>
                <span className="allocation-description">{alloc.description}</span>
                <div className="allocation-stats">
                  <div className="stat-row">
                    <span className="stat-label">Actual:</span>
                    <span className="allocation-percentage">{alloc.percentage.toFixed(1)}%</span>
                    <span className="allocation-amount">
                      ${Number(formatUnits(alloc.amount, 6)).toLocaleString()}
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Target:</span>
                    <span className="target-percentage">{alloc.targetAllocation.toFixed(1)}%</span>
                    {Math.abs(alloc.percentage - alloc.targetAllocation) > 5 && (
                      <span className="rebalance-indicator" title="Rebalancing needed">⚖️</span>
                    )}
                  </div>
                </div>
                <div className="allocation-address">
                  <span className="address-label">Contract:</span>
                  <span className="address-value">{alloc.address.slice(0, 6)}...{alloc.address.slice(-4)}</span>
                </div>
              </div>
            </div>
          ))}
          
          {/* Show emergency idle funds if any */}
          {emergencyIdleFunds > 0n && (
            <div className="allocation-item emergency" style={{'--color': '#FF6B6B'}}>
              <div className="allocation-color" style={{ backgroundColor: '#FF6B6B' }}>
                <span className="protocol-icon">⚠️</span>
              </div>
              <div className="allocation-info">
                <div className="allocation-header">
                  <span className="allocation-name">Emergency Idle</span>
                  <FaExclamationCircle className="health-icon warning" title="Funds awaiting reallocation" />
                </div>
                <span className="allocation-description">Funds withdrawn from unhealthy adapters</span>
                <div className="allocation-stats">
                  <div className="stat-row">
                    <span className="stat-label">Amount:</span>
                    <span className="allocation-percentage">
                      {((Number(emergencyIdleFunds) / Number(totalAssets)) * 100).toFixed(1)}%
                    </span>
                    <span className="allocation-amount">
                      ${Number(formatUnits(emergencyIdleFunds, 6)).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="allocations-summary">
        <div className="summary-item">
          <span className="summary-label">Total Value Locked:</span>
          <span className="summary-value">${Number(formatUnits(totalAssets, 6)).toLocaleString()}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Active Strategies:</span>
          <span className="summary-value">{allocations.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Allocation:</span>
          <span className="summary-value">{totalAllocationBps ? Number(totalAllocationBps) / 100 : 100}%</span>
        </div>
      </div>

      <div className="allocations-footer">
        <div className="footer-item">
          <span>Strategy Type</span>
          <span>Multi-Protocol Yield</span>
        </div>
        <div className="footer-item">
          <span>Risk Level</span>
          <span className="risk-low">Low-Medium</span>
        </div>
        <div className="footer-item">
          <span>Rebalancing</span>
          <span>Automated</span>
        </div>
      </div>
    </div>
  );
}

export default VaultAllocationsV2;